import { Maximize2, Minimize2, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Map as MapComponent, MapRef } from '@/components/territories/map/Map';
import { TerritoriesByState } from '@/components/territories/TerritoriesByState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TERRITORIES_TOUR_CLASSES } from '@/constants/tour/classNames';
import { useGetTerritories } from '@/hooks/territories/useGetTerritories';
import { toast } from '@/hooks/ui/use-toast';
import { useGetZorMeetingLink } from '@/hooks/zor-general-setting/useGetZorMeetingLink';
import { useGetZorTerritorySettings } from '@/hooks/zor-territory-settings/useGetZorTerritorySettings';
import { cn } from '@/lib/ui/utils';
import { useTerritoryStore } from '@/stores/useTerritoryStore';
import useZorMeetingLinkStore from '@/stores/useZorMeetingLinkStore';
import { useZorTerritorySettingsStore } from '@/stores/useZorTerritorySettingsStore';
import { Territory } from '@/types/territories/territory.type';

interface MapCenter {
  lat: number;
  lng: number;
}

const DEFAULT_CENTER: MapCenter = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;

const useDebounce = <T,>(value: T, delay: number = 500): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const DashboardTerritories = () => {
  const location = useLocation();
  const selectedTerritory = location.state?.selectedTerritory; // retrieve passed object
  const mapRef = useRef<MapRef>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Add state
  const { setTerritories, focusedTerritory, setSelectedTerritory } =
    useTerritoryStore();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const { setMeetingLink } = useZorMeetingLinkStore();
  const debouncedSearchValue = useDebounce(searchValue);

  const {
    result: territoriesResult,
    isError: isTerritoriesError,
    isLoading: loadingTerritories,
    isSuccess: isTerritoriesSuccess,
  } = useGetTerritories({
    ...(debouncedSearchValue && { search: debouncedSearchValue }),
  });

  // 👇 decide initial props
  const initialCenter =
    focusedTerritory && selectedTerritory
      ? {
          lat: focusedTerritory.centroid.coordinates[1],
          lng: focusedTerritory.centroid.coordinates[0],
        }
      : DEFAULT_CENTER;

  const initialZoom = focusedTerritory && selectedTerritory ? 10 : DEFAULT_ZOOM;

  // ZorTerritorySettings data
  const {
    result: zorTerritorySettingsResult,
    isError: isTerritorySettingsError,
    isSuccess: isTerritorySettingsSuccess,
  } = useGetZorTerritorySettings();
  const { setZorTerritorySettings } = useZorTerritorySettingsStore();

  // Set ZorTerritorySettings data to store
  useEffect(() => {
    if (zorTerritorySettingsResult?.data && isTerritorySettingsSuccess) {
      setZorTerritorySettings(zorTerritorySettingsResult.data);
    }
  }, [zorTerritorySettingsResult, setZorTerritorySettings]);

  useEffect(() => {
    if (isTerritorySettingsError) {
      toast({
        title: 'Error loading zor territory settings',
        variant: 'destructive',
      });
    }
  }, [isTerritorySettingsError]);

  const {
    result: meetingLinkResult,
    isError: isGetError,
    isSuccess: isGetSuccess,
  } = useGetZorMeetingLink();

  useEffect(() => {
    if (isGetSuccess && meetingLinkResult?.data) {
      if (meetingLinkResult.data.meeting_link) {
        setMeetingLink(meetingLinkResult.data.meeting_link);
      }
    }
  }, [isGetSuccess, meetingLinkResult, setMeetingLink]);

  useEffect(() => {
    if (isGetError) {
      toast({
        title: 'Error loading meeting link',
        variant: 'destructive',
      });
    }
  }, [isGetError]);

  useEffect(() => {
    if (territoriesResult?.data && isTerritoriesSuccess) {
      setTerritories(territoriesResult.data);
    }
  }, [territoriesResult, setTerritories, isTerritoriesSuccess]);

  useEffect(() => {
    if (isTerritoriesError) {
      toast({
        title: 'Error loading territories',
        variant: 'destructive',
      });
    }
  }, [isTerritoriesError]);

  const handleToggleExpand = (): void => {
    setIsExpanded(!isExpanded);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(e.target.value);
  };

  const handleTerritoryClick = (territory: Territory): void => {
    mapContainerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    if (mapRef.current && territory.centroid) {
      mapRef.current.flyTo({
        lat: territory.centroid.coordinates[1],
        lng: territory.centroid.coordinates[0],
      });
    }

    setTimeout(() => {
      setSelectedTerritory(territory);
    }, 800);
  };
  useEffect(() => {
    if (focusedTerritory && mapRef.current) {
      mapRef.current.flyTo({
        lat: focusedTerritory.centroid.coordinates[1],
        lng: focusedTerritory.centroid.coordinates[0],
      });
    } else if (mapRef.current) {
      mapRef.current.flyTo(DEFAULT_CENTER, DEFAULT_ZOOM);
    }
  }, [focusedTerritory]);

  return (
    <div className="space-y-6">
      <div
        className={`${TERRITORIES_TOUR_CLASSES.PAGE_HEADER} flex w-full flex-col justify-between gap-4 md:flex-row`}
      >
        <div className="max-w-md">
          <h2 className="as-strong">Territories</h2>
          <p className="as-title-02 mt-2 font-light text-typo-gray-tertiary">
            Information about territories set by franchisors!
          </p>
        </div>
      </div>
      <div className="space-y-6">
        <div
          ref={mapContainerRef}
          className={cn(
            `${TERRITORIES_TOUR_CLASSES.MAP_CONTAINER} relative rounded-lg transition-all duration-300`,
            isExpanded ? 'h-[80vh]' : 'h-80'
          )}
        >
          <MapComponent
            ref={mapRef}
            zoom={initialZoom}
            center={initialCenter}
            onTerritoryClick={handleTerritoryClick}
          />
          <Button
            className={`${TERRITORIES_TOUR_CLASSES.MAP_EXPAND_BUTTON} absolute right-2 top-2`}
            onClick={handleToggleExpand}
          >
            {isExpanded ? <Minimize2 /> : <Maximize2 />}
            {isExpanded ? 'Minimize' : 'Expand'}
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div className="relative flex w-full items-center">
            <Search size={16} className="absolute left-3 text-gray-500" />
            <Input
              type="text"
              value={searchValue}
              className={`${TERRITORIES_TOUR_CLASSES.SEARCH_INPUT} pl-8`}
              placeholder="Search by territory number, state or zip"
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div
          className={`${TERRITORIES_TOUR_CLASSES.BY_STATE_CONTAINER} space-y-1.5`}
        >
          <TerritoriesByState
            loading={loadingTerritories}
            onTerritoryClick={handleTerritoryClick}
          />
        </div>
      </div>
    </div>
  );
};

export { DashboardTerritories };
